'use client';

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';
import { AppBar } from '@/components/ui/AppBar';

export interface InfoPopoverProps {
  /** Whether the popover is open */
  open: boolean;
  /** Callback when popover should close */
  onOpenChange?: (open: boolean) => void;
  /** Popover content */
  children: React.ReactNode;
  /** Optional title shown in the header */
  label?: string;
  /** Optional leading slot or back handler for the app bar */
  leading?: React.ReactNode;
  /** Optional trailing slot or close handler for the app bar */
  trailing?: React.ReactNode;
  /** Click handler to show a default back icon button on the left */
  onBack?: () => void;
  /** Click handler for close button */
  onClose?: () => void;
  /** Whether the popover can be dismissed by clicking outside or pressing ESC */
  dismissible?: boolean;
  /** Custom class names */
  contentClassName?: string;
  bodyClassName?: string;
  /** Optional position adjustments (in pixels) */
  position?: {
    top?: number;
    left?: number;
    right?: number;
    bottom?: number;
  };
  /** Optional anchor element reference for positioning */
  anchorRef?: React.RefObject<HTMLElement | null>;
  /** Placement strategy: 'below' (default), 'above', 'center' (centers relative to anchor) */
  placement?: 'below' | 'above' | 'center';
  /** Horizontal alignment: 'left' (default), 'center', 'right' */
  align?: 'left' | 'center' | 'right';
  /** Offset in pixels from the anchor (default: 8 for below/above, 0 for center) */
  offset?: number;
  /** Horizontal offset in pixels (positive = right, negative = left) */
  offsetX?: number;
  /** Vertical offset in pixels (positive = down, negative = up) */
  offsetY?: number;
}

export function InfoPopover({
  open,
  onOpenChange,
  children,
  label,
  leading,
  trailing,
  onBack,
  onClose,
  dismissible = true,
  contentClassName,
  bodyClassName,
  position,
  anchorRef,
  placement = 'below',
  align = 'center',
  offset,
  offsetX = 0,
  offsetY = 0,
}: InfoPopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [calculatedPosition, setCalculatedPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [isPositioned, setIsPositioned] = useState(false);

  // Calculate position synchronously before paint to prevent glitch
  useLayoutEffect(() => {
    if (!open) {
      setCalculatedPosition(null);
      setIsPositioned(false);
      return;
    }

    if (position) {
      // Use custom position if provided
      setCalculatedPosition({
        top: position.top ?? 0,
        left: position.left ?? 0,
      });
      setIsPositioned(true);
      return;
    }

    if (anchorRef?.current) {
      // Calculate position immediately using anchor dimensions
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const spacing = offset !== undefined ? offset : 8;

      // Initial calculation using estimated popover dimensions (400px width)
      const estimatedPopoverWidth = 400;
      const estimatedPopoverHeight = 100;

      // Calculate horizontal alignment
      let left: number;
      if (align === 'left') {
        left = anchorRect.left;
      } else if (align === 'right') {
        left = anchorRect.right - estimatedPopoverWidth;
      } else {
        // Default: center
        left = anchorRect.left + anchorRect.width / 2 - estimatedPopoverWidth / 2;
      }

      // Apply horizontal offset
      left += offsetX;

      // Calculate vertical position
      let top: number;
      if (placement === 'center') {
        top = anchorRect.top + anchorRect.height / 2 - estimatedPopoverHeight / 2;
      } else if (placement === 'above') {
        top = anchorRect.top - estimatedPopoverHeight - spacing;
      } else {
        // Default: position below the anchor
        top = anchorRect.bottom + spacing;
      }

      // Apply vertical offset
      top += offsetY;

      // Set initial position immediately to prevent glitch
      setCalculatedPosition({ top, left });
      setIsPositioned(true);
    }
  }, [open, anchorRef, position, placement, align, offset, offsetX, offsetY]);

  // Refine position once popover is rendered with actual dimensions
  useEffect(() => {
    if (!open || !anchorRef?.current || !popoverRef.current || position) return;

    requestAnimationFrame(() => {
      if (!anchorRef?.current || !popoverRef.current) return;

      const anchorRect = anchorRef.current.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();
      const spacing = offset !== undefined ? offset : 8;

      // Recalculate with actual dimensions
      let refinedLeft: number;
      if (align === 'left') {
        refinedLeft = anchorRect.left;
      } else if (align === 'right') {
        refinedLeft = anchorRect.right - popoverRect.width;
      } else {
        refinedLeft = anchorRect.left + anchorRect.width / 2 - popoverRect.width / 2;
      }

      // Apply horizontal offset
      refinedLeft += offsetX;

      let refinedTop: number;
      if (placement === 'center') {
        refinedTop = anchorRect.top + anchorRect.height / 2 - popoverRect.height / 2;
      } else if (placement === 'above') {
        refinedTop = anchorRect.top - popoverRect.height - spacing;
      } else {
        refinedTop = anchorRect.bottom + spacing;
      }

      // Apply vertical offset
      refinedTop += offsetY;

      // Adjust if popover would go off screen
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (refinedLeft + popoverRect.width > viewportWidth) {
        refinedLeft = viewportWidth - popoverRect.width - 16;
      }

      if (refinedLeft < 16) {
        refinedLeft = 16;
      }

      if (refinedTop + popoverRect.height > viewportHeight) {
        refinedTop = anchorRect.top - popoverRect.height - 8;
      }

      if (refinedTop < 16) {
        refinedTop = 16;
      }

      setCalculatedPosition({ top: refinedTop, left: refinedLeft });
    });
  }, [open, anchorRef, position, placement, align, offset, offsetX, offsetY]);

  // Handle ESC key
  useEffect(() => {
    if (!open || !dismissible) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange?.(false);
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, dismissible, onOpenChange, onClose]);

  // Handle click outside
  useEffect(() => {
    if (!open || !dismissible) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        // Check if click is not on the anchor element
        if (anchorRef?.current && anchorRef.current.contains(e.target as Node)) {
          return;
        }
        onOpenChange?.(false);
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, dismissible, onOpenChange, onClose, anchorRef]);

  if (!open) return null;

  const style: React.CSSProperties = {};
  if (calculatedPosition) {
    style.top = `${calculatedPosition.top}px`;
    style.left = `${calculatedPosition.left}px`;
  } else if (position) {
    if (position.top !== undefined) style.top = `${position.top}px`;
    if (position.left !== undefined) style.left = `${position.left}px`;
    if (position.right !== undefined) style.right = `${position.right}px`;
    if (position.bottom !== undefined) style.bottom = `${position.bottom}px`;
  } else {
    // Default position if no anchor or position provided
    style.top = '50%';
    style.left = '50%';
    style.transform = 'translate(-50%, -50%)';
  }

  const popoverContent = (
    <div
      ref={popoverRef}
      className={cn(
        'fixed z-50',
        'flex flex-col',
        'w-[400px]',
        'rounded-3xl border border-tertiary bg-primary',
        'shadow-lg',
        contentClassName,
      )}
      style={style}
      role="dialog"
      aria-modal="true"
      aria-labelledby={label ? 'popover-title' : undefined}
    >
      {/* Header */}
      {label && (
        <AppBar
          className="px-4"
          title={label}
          leading={leading}
          trailing={trailing}
          {...(onBack ? { onBack } : {})}
          {...(onClose ? { onClose } : {})}
        />
      )}

      {/* Body */}
      <div
        className={cn(
          'flex-1 min-h-0 overflow-y-auto',
          'text-secondary text-body-2 px-6 py-4',
          bodyClassName,
        )}
      >
        {children}
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(popoverContent, document.body) : null;
}

export default InfoPopover;
